// Promo / attribution capture
//
// Detects ?promo=xxx in the URL (e.g. from a printed QR code in The 99 book,
// a magazine ad, etc.) and persists it to localStorage so the attribution
// survives an OAuth redirect (Google/Facebook sign-in strips the URL params).
//
// Public API:
//   capturePromoFromUrl()      — call once on app boot, before auth
//   getAttribution()           — { source, capturedAt } | null
//   getPromoConfig(source)     — { label, bannerMessage } | null
//   isPromoSupported(source)   — boolean
//   hasSeenBanner(source)      — boolean
//   markBannerSeen(source)     — void
//   getCurrentSessionAttribution() — { source, capturedAt } | null
//                                    (only set if ?promo=xxx was captured
//                                     this browser session)
//   getPendingBookToken()      — signed book-QR token string | null
//   clearPendingBookToken()    — void (after a successful redeem)
//   clearAttribution()         — void (for tests / sign-out flows)

const STORAGE_KEY = "the99.promo.attribution";
const SEEN_BANNERS_KEY = "the99.promo.seenBanners";
const SESSION_KEY = "the99.promo.attribution.session";
// Signed book-QR token (the99book.<payload>.<sig>) captured from ?bookToken=.
// Persisted so it survives the OAuth redirect roundtrip, then handed to the
// backend /v1/book/redeem endpoint after the user authenticates.
const BOOK_TOKEN_KEY = "the99.book.token";

// Allowlist of supported promo codes. Adding a new source = adding one entry
// here (no other code changes needed).
const PROMO_CONFIG = {
  book: {
    label: "The 99 book",
    bannerMessage:
      "Welcome! You've unlocked special access from The 99 book. Enjoy exploring exclusive features.",
  },
  // Example future entries:
  // magazine: { label: "Bassmaster Magazine", bannerMessage: "Welcome! You came from Bassmaster — enjoy exploring The 99." },
  // event2026: { label: "ICAST 2026", bannerMessage: "Thanks for visiting our booth at ICAST 2026!" },
};

function safeGetItem(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore quota / private-mode errors */
  }
}

function safeGetSession(key) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetSession(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore quota / private-mode errors */
  }
}

export function getPromoConfig(source) {
  if (!source) return null;
  return PROMO_CONFIG[source] || null;
}

export function isPromoSupported(source) {
  return Boolean(getPromoConfig(source));
}

export function getAttribution() {
  const raw = safeGetItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.source || !isPromoSupported(parsed.source)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Capture ?promo=xxx from the current URL into localStorage. Subsequent
 * navigations / OAuth redirects retain the attribution. Idempotent: only
 * the first valid capture wins (don't overwrite an existing attribution if
 * the user later opens a different promo URL — first-touch attribution).
 *
 * Returns the current attribution after capture (or null).
 */
export function capturePromoFromUrl() {
  if (typeof window === "undefined") return null;

  let promo = null;
  let bookToken = null;
  try {
    const params = new URLSearchParams(window.location.search);
    promo = params.get("promo");
    bookToken = params.get("bookToken");
  } catch {
    promo = null;
    bookToken = null;
  }

  // A scanned book QR encodes ?bookToken=<signed>. Treat its presence as the
  // "book" attribution source (so the signup checkbox auto-confirms) and stash
  // the raw token for later server-side redemption.
  if (bookToken && /^the99book\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(bookToken)) {
    safeSetItem(BOOK_TOKEN_KEY, bookToken);
    if (!promo) promo = "book";
  }

  if (!promo || !isPromoSupported(promo)) {
    return getAttribution();
  }

  const existing = getAttribution();
  if (existing) {
    // First-touch attribution wins, but mark this session as a verified
    // promo arrival so the AuthScreen checkbox can show as checked even
    // when the persistent record predates the current visit.
    safeSetSession(SESSION_KEY, JSON.stringify(existing));
    return existing;
  }

  const attribution = {
    source: promo,
    capturedAt: Date.now(),
  };
  safeSetItem(STORAGE_KEY, JSON.stringify(attribution));
  safeSetSession(SESSION_KEY, JSON.stringify(attribution));
  return attribution;
}

/**
 * Returns the attribution captured during the current browser session
 * (i.e. the user landed on the app this session via ?promo=xxx). Survives
 * an OAuth redirect within the same tab, but a brand-new app boot without
 * the URL param will return null even if localStorage still has a stale
 * attribution from earlier testing or a previous QR scan.
 *
 * Use this for UI confirmation (e.g. "verified book buyer" indicator) and
 * for write-time attribution on enrollment, so we never auto-attribute a
 * fresh signup based on someone else's leftover localStorage.
 */
export function getCurrentSessionAttribution() {
  const raw = safeGetSession(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.source || !isPromoSupported(parsed.source)) return null;
    return parsed;
  } catch {
    return null;
  }
}

function readSeenBanners() {
  const raw = safeGetItem(SEEN_BANNERS_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function hasSeenBanner(source) {
  if (!source) return false;
  return Boolean(readSeenBanners()[source]);
}

export function markBannerSeen(source) {
  if (!source) return;
  const next = { ...readSeenBanners(), [source]: Date.now() };
  safeSetItem(SEEN_BANNERS_KEY, JSON.stringify(next));
}

export function clearAttribution() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(SEEN_BANNERS_KEY);
    localStorage.removeItem(BOOK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Returns the signed book-QR token captured from ?bookToken=, or null. The
 * caller passes this to the backend /v1/book/redeem endpoint after the user
 * signs in. Kept separate from attribution so a stale token can be cleared
 * once redeemed without wiping first-touch attribution.
 */
export function getPendingBookToken() {
  const raw = safeGetItem(BOOK_TOKEN_KEY);
  return raw && typeof raw === "string" ? raw : null;
}

export function clearPendingBookToken() {
  try {
    localStorage.removeItem(BOOK_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
